import type { ReactNode } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import {
  AGENT_MODES, API_FORMATS, LIBRARY_MODES, MCP_FORMATS, USUAL_NAME, entersKey, isNamedKey, nameFor, usesKey,
  type KeyFormat, type SignInMode, type SignInScope, type SignInValue,
} from '../utils/signIn';
import '@modules/connector_library/components/LibraryOrganizations.css';

/**
 * How a connector signs in to its service — ONE block, asked in order (item 80):
 *
 *   1  Who signs in?  A connector in an organization's list: no sign-in, one
 *      key for everyone, a key for each agent, each person with their own
 *      account, each person with their own key. A connector added on one
 *      agent: no sign-in, or a key for this agent.
 *   2  How does the service expect the key?  Only for the choices with a key.
 *      An API: in the Authorization header ("Bearer"), in a header it names,
 *      in the address, or a username and password (item 69's words). An MCP
 *      server: in the Authorization header, or in a header it names.
 *   3  The key — only where it is entered here: the one shared key (a list's
 *      connector) or the agent's own key. Everyone else's key is entered by
 *      whoever owns it.
 *
 * The block holds the answer; its host turns it into the fields it saves —
 * the same fields, in the same shapes, as before this block existed.
 */

interface Props {
  kind: 'api' | 'mcp';
  scope: SignInScope;
  value: SignInValue;
  onChange: (next: SignInValue) => void;
  /** Read-only (an organization that did not add it), or while saving. */
  disabled?: boolean;
  /** A key is stored and stays unless a new one is typed. */
  keyStored?: boolean;
  /** An MCP server being added: a key given only to list its tools, not kept. */
  checkKey?: boolean;
  /** Shown under the choices (each person signing in: the API's sign-in addresses). */
  children?: ReactNode;
  name: string;
}

export function SignInChoice({ kind, scope, value, onChange, disabled, keyStored, checkKey, children, name }: Props) {
  const { t } = useLanguage();
  const set = (patch: Partial<SignInValue>) => onChange({ ...value, ...patch });
  const modes = scope === 'library' ? LIBRARY_MODES : AGENT_MODES;
  const formats = kind === 'api' ? API_FORMATS : MCP_FORMATS;
  const keyed = usesKey(value.mode);
  const askKey = keyed && entersKey(scope, value.mode);
  const askCheckKey = checkKey && keyed && !askKey;
  // Which key this is: the one shared, the agent's own, or one only to check an MCP server with.
  const keyStep = askCheckKey ? 'checkKey' : scope === 'library' ? 'sharedKey' : 'agentKey';
  const modeText = (m: SignInMode, part: 'title' | 'hint') => (scope === 'agent'
    ? t(`connectorLibrary.signin.agentMode.${m}.${part}`)
    : t(part === 'title' ? `connectorLibrary.signin.mode.${m}` : `connectorLibrary.signin.hint.${m}`));
  const formatText = (f: KeyFormat, part: 'title' | 'hint') => (kind === 'api'
    ? t(`bots.connectors.api.authChoice.${f}.${part}`)
    : t(`connectorLibrary.signin.mcpFormat.${f}.${part}`));
  // The usual name follows the choice until someone types their own.
  const pickFormat = (f: KeyFormat) => set({ format: f, keyName: nameFor(kind, f, value.keyName) });

  return (
    <div className="cxl-signin" data-testid={`signin-choice-${scope}`}>
      <p className="cxl-signin__step">{t('connectorLibrary.signin.step.who')}</p>
      <div className="cxl-orgs__modes" role="radiogroup" aria-label={t('connectorLibrary.signin.step.who')}>
        {modes.map(m => (
          <label key={m} className={`cxl-orgs__mode ${value.mode === m ? 'is-on' : ''}`}
                 data-testid={`${scope}-signin-${m}`}>
            <input type="radio" name={`${name}-mode`} checked={value.mode === m} disabled={disabled}
                   onChange={() => set({ mode: m })} />
            <span className="cxl-orgs__mode-text">
              <span className="cxl-orgs__mode-title">{modeText(m, 'title')}</span>
              <span className="cxl-orgs__mode-hint">{modeText(m, 'hint')}</span>
            </span>
          </label>
        ))}
      </div>
      {scope === 'agent' && (
        <p className="cxl-signin__note" data-testid="agent-signin-per-person">{t('connectorLibrary.signin.agentNote')}</p>
      )}
      {children}

      {keyed && (
        <>
          <p className="cxl-signin__step">{t('connectorLibrary.signin.step.format')}</p>
          <div className="cxd__auth" role="radiogroup" aria-label={t('connectorLibrary.signin.step.format')}
               data-testid="signin-format">
            {formats.map(f => (
              <label key={f} className={`cxd__auth-opt ${value.format === f ? 'is-on' : ''}`} data-testid={`signin-format-${f}`}>
                <input type="radio" name={`${name}-format`} checked={value.format === f} disabled={disabled}
                       onChange={() => pickFormat(f)} />
                <span className="cxd__auth-text">
                  <span className="cxd__auth-title">{formatText(f, 'title')}</span>
                  <span className="cxd__auth-hint">{formatText(f, 'hint')}</span>
                </span>
              </label>
            ))}
          </div>
          {isNamedKey(value.format) && (
            <label className="cxl-signin__field">
              <span>{t(value.format === 'query' ? 'bots.connectors.api.auth.paramName' : 'bots.connectors.api.auth.headerName')}</span>
              <input className="form-input form-input--mono" dir="ltr" value={value.keyName} disabled={disabled}
                     placeholder={USUAL_NAME[value.format]} onChange={e => set({ keyName: e.target.value })}
                     data-testid="signin-key-name" />
            </label>
          )}
        </>
      )}

      {(askKey || askCheckKey) && (
        <>
          <p className="cxl-signin__step">
            {t(`connectorLibrary.signin.step.${keyStep}`)}
          </p>
          {value.format === 'basic' ? (
            <div className="cxd__row cxd__row--halves">
              <input className="form-input" dir="ltr" autoComplete="off" value={value.username} disabled={disabled}
                     placeholder={keyStored ? t('connectorLibrary.signin.keyStored') : t('bots.connectors.api.auth.username')}
                     aria-label={t('bots.connectors.api.auth.username')} onChange={e => set({ username: e.target.value })}
                     data-testid="signin-key-username" />
              <input type="password" className="form-input" dir="ltr" autoComplete="new-password" value={value.password}
                     disabled={disabled} placeholder={t('bots.connectors.api.auth.password')}
                     aria-label={t('bots.connectors.api.auth.password')} onChange={e => set({ password: e.target.value })}
                     data-testid="signin-key-password" />
            </div>
          ) : (
            <input type="password" className="form-input form-input--mono" dir="ltr" autoComplete="new-password"
                   value={value.value} disabled={disabled}
                   placeholder={keyStored ? t('connectorLibrary.signin.keyStored')
                     : kind === 'mcp' && value.format === 'bearer' ? t('bots.connectors.mcp.authValuePlaceholder')
                       : t('bots.connectors.api.auth.valuePlaceholder')}
                   aria-label={t('bots.connectors.api.auth.valuePlaceholder')} onChange={e => set({ value: e.target.value })}
                   data-testid="signin-key-value" />
          )}
          <p className="cxl-signin__note" data-testid="signin-key-note">
            {t(`connectorLibrary.signin.${keyStep}Note`)}
          </p>
        </>
      )}
      {scope === 'library' && keyed && !askKey && !askCheckKey && (
        <p className="cxl-signin__note" data-testid="signin-no-key-here">{t(`connectorLibrary.signin.noKeyHere.${value.mode}`)}</p>
      )}
    </div>
  );
}

/** The Settings tab's one line about sign-in, e.g. "One key for everyone · In a header the service names (X-Api-Key)". */
export function SignInSummary({ kind, value }: { kind: 'api' | 'mcp'; value: SignInValue }) {
  const { t } = useLanguage();
  const who = t(`connectorLibrary.signin.mode.${value.mode}`);
  if (!usesKey(value.mode)) return <>{who}</>;
  const how = kind === 'api' ? t(`bots.connectors.api.authChoice.${value.format}.title`)
    : t(`connectorLibrary.signin.mcpFormat.${value.format}.title`);
  return (
    <>
      {who} · {how}
      {isNamedKey(value.format) && value.keyName && <> (<bdi dir="ltr">{value.keyName}</bdi>)</>}
    </>
  );
}
